<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("STOCK.PACKAGINGS.TITLE") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('stock-packagings-create')"
          class="mr-4 success"
          :disabled="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="stock-packagings-table"
              height="500"
              name="stock-packagings-table"
              :headers="headers"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{ method: tableItemsMethod }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in headers"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <span
                  v-if="header.value === 'options'"
                  :key="index"
                  class="d-flex align-center justify-center flex-nowrap"
                >
                  <v-btn
                    v-if="hasPermissions('stock-packagings-edit')"
                    icon
                    color="primary"
                    @click="handleEditClick(item.item.id)"
                  >
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="hasPermissions('stock-packagings-delete') && item.item.can_delete"
                    icon
                    color="error"
                    @click="handleDeleteClick(item.item)"
                  >
                    <v-icon dark>mdi-delete</v-icon>
                  </v-btn>
                </span>
                <template v-else-if="header.value === 'is_active'">
                  <v-chip :key="index" x-small :color="item.value ? 'success' : 'error'" dark>
                    {{ item.value ? $t("GENERAL.LABELS.ACTIVE") : $t("GENERAL.LABELS.INACTIVE") }}
                  </v-chip>
                </template>
                <template v-else-if="header.value === 'is_system'">
                  <v-chip :key="index" x-small :color="item.value ? 'warning' : 'grey'" dark>
                    {{ item.value ? $t("STOCK.PACKAGINGS.SYSTEM") : $t("STOCK.PACKAGINGS.CUSTOM") }}
                  </v-chip>
                </template>
                <template v-else>
                  {{ item.value || item.value === 0 ? item.value : "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <TPConfirm
      v-model="showDeleteConfirm"
      :content="$t('GENERAL.DELETE_CONFIRM')"
      @confirm="deletePackaging"
    ></TPConfirm>
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import userStateService from "@/common/userState.service";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  DELETE_STOCK_PACKAGING,
  GET_STOCK_PACKAGINGS,
} from "@/store/stock/stockPackagings.module";

export default {
  name: "StockListPackagings",
  components: {
    Table,
    TPConfirm,
  },
  data() {
    return {
      tableName: "stock-packagings-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      showDeleteConfirm: false,
      selectedPackaging: null,
      tableItemsMethod: GET_STOCK_PACKAGINGS,
      headers: [
        { text: "#", value: "id", align: "center", width: "10%" },
        { text: this.$t("GENERAL.LABELS.NAME"), value: "name", align: "center", width: "30%" },
        {
          text: this.$t("STOCK.PACKAGINGS.USAGE_COUNT"),
          value: "usage_count",
          sortable: false,
          align: "center",
          width: "15%",
        },
        { text: this.$t("GENERAL.LABELS.STATUS"), value: "is_active", align: "center", width: "15%" },
        { text: this.$t("STOCK.PACKAGINGS.TYPE"), value: "is_system", align: "center", width: "15%" },
        { text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"), value: "options", sortable: false, align: "center", width: "15%" },
      ],
    };
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );
    this.userStatePrepared = true;
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];
        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }
      if (error && error.message) {
        return error.message;
      }
      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    reloadTable() {
      this.$nextTick(() => this.$refs["stock-packagings-table"]?.fetchServerData());
    },
    handleCreateClick() {
      this.$router.push({ name: "stock-packagings-create" });
    },
    handleEditClick(id) {
      userStateService.setState(`table-state.${this.tableName}.blinkRowItemKey`, id);
      this.$router.push({ name: "stock-packagings-edit", params: { id } });
    },
    handleDeleteClick(packaging) {
      this.selectedPackaging = packaging;
      this.showDeleteConfirm = true;
    },
    deletePackaging() {
      if (!this.selectedPackaging) {
        return;
      }
      this.isProcessing = true;
      this.$store
        .dispatch(DELETE_STOCK_PACKAGING, this.selectedPackaging.id)
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
          this.reloadTable();
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.selectedPackaging = null;
        });
    },
  },
};
</script>
